import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ProfileData } from "@/hooks/useProfileSettings";
import { Loader2, ExternalLink, Send } from "lucide-react";
import { useTranslation } from "react-i18next";

interface TelegramConnectionStatusProps {
  profile: ProfileData | null;
  connectionAttempted?: boolean;
  connectingTelegram: boolean;
  onConnectTelegram: () => void;
  onOpenTelegramDirectly: () => void;
}

const TelegramConnectionStatus = ({
  profile,
  connectionAttempted = false,
  connectingTelegram,
  onConnectTelegram,
  onOpenTelegramDirectly
}: TelegramConnectionStatusProps) => {
  const { t } = useTranslation();

  if (profile?.telegram_chat_id) {
    return (
      <Alert className="bg-green-50 border-green-100">
        <AlertTitle className="text-green-700 font-medium">{t('settings.telegramConnected')}</AlertTitle>
        <AlertDescription className="text-green-700">
          {t('settings.telegramConnectedDesc')}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col items-center gap-3 py-2">
      {connectionAttempted && (
        <Alert className="bg-yellow-50 border-yellow-100">
          <AlertTitle className="text-yellow-700 font-medium">{t('settings.telegramConnectionPending')}</AlertTitle>
          <AlertDescription className="text-yellow-700">
            {t('settings.telegramConnectionPendingDesc')}
          </AlertDescription>
        </Alert>
      )}
      
      <Button 
        variant="outline" 
        size="sm"
        onClick={onConnectTelegram}
        disabled={connectingTelegram}
        className="flex items-center space-x-2 w-full md:w-auto"
      >
        {connectingTelegram ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>{t('settings.connectingTelegram')}</span>
          </>
        ) : (
          <>
            <Send className="h-4 w-4" />
            <span>{t('settings.connectToTelegramAssistant')}</span>
          </>
        )}
      </Button>
      
      <p className="text-xs text-gray-500 max-w-md text-center">
        {t('settings.telegramConnectionInstructions')}
      </p>
      
      <Button
        variant="link"
        size="sm"
        onClick={onOpenTelegramDirectly}
        className="text-xs flex items-center"
      >
        <ExternalLink className="h-3 w-3 mr-1" />
        <span>{t('settings.alreadyHaveTelegram')}</span>
      </Button>
    </div>
  );
};

export default TelegramConnectionStatus;
